import { useState, useEffect } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { Menu, ArrowUpRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { ThemeToggle } from '@/components/layout/ThemeToggle'
import { cn } from '@/lib/utils'
import { LogoIcon } from './Logo'

const navLinks = [
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'Docs', href: '/docs' },
  { label: 'Live demo', href: '/test' },
  { label: 'Explorer', href: '/explorer' },
]

function NavLink({
  href,
  children,
  className,
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  const { url } = usePage()
  const isHome = url === '/'

  if (href.startsWith('/#')) {
    const hash = href.slice(1)
    return (
      <a
        href={href}
        onClick={(e) => {
          if (isHome) {
            e.preventDefault()
            document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' })
          }
        }}
        className={className}
      >
        {children}
      </a>
    )
  }

  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  )
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-3 pt-3 sm:px-6 sm:pt-5">
      <div
        className={cn(
          'mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 rounded-xl pl-5 pr-3 transition-all duration-300',
          scrolled
            ? 'border border-foreground/10 bg-background/70 backdrop-blur-xl shadow-[0_8px_32px_-8px_hsl(var(--brand-2)/0.18)]'
            : 'border border-transparent bg-transparent',
        )}
      >
        {/* Logo */}
        <Link
          href="/"
          className="flex items-center gap-2 font-heading text-[15px] font-semibold tracking-tight"
        >
          <LogoIcon />
          <span>KonexZero</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <NavLink
              key={link.href}
              href={link.href}
              className="rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop actions */}
        <div className="hidden items-center gap-1.5 md:flex">
          <ThemeToggle />
          <Button variant="ghost" size="sm" asChild className="h-9 rounded-md px-3 text-sm">
            <Link href="/sign_in">Log in</Link>
          </Button>
          <Button size="sm" asChild className="h-9 rounded-md px-4 text-sm">
            <Link href="/sign_up">
              Sign up
              <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle />
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-md">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-8 flex flex-col gap-1">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.href}
                    href={link.href}
                    className="rounded-md px-3 py-2 text-base text-muted-foreground hover:bg-foreground/5 hover:text-foreground transition"
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-2">
                <Button variant="outline" asChild>
                  <Link href="/sign_in">Log in</Link>
                </Button>
                <Button asChild>
                  <Link href="/sign_up">Sign up</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
